import type { ReactNode } from 'react';
import { Check, AlertCircle } from 'lucide-react';
import { cn } from '../utils/cn';

export interface ArrowStep {
  key: string;
  label: ReactNode;
  description?: ReactNode;
  /**
   * Whether the step is allowed to be jumped to. Defaults to allowed
   * if it's the active step or any visited / saved step.
   */
  enabled?: boolean;
}

export interface ArrowTabStepperProps {
  steps: ArrowStep[];
  activeIndex: number;
  /**
   * Indices the user committed via "Save & Next" — render with the
   * GREEN success colour and a check icon.
   */
  savedIndices?: number[];
  /**
   * Indices the user merely advanced past via "Next" (without saving)
   * or revisited without committing — render with the RED warning
   * colour and an alert icon. Useful for showing "you skipped this".
   */
  visitedIndices?: number[];
  /**
   * Backwards-compat alias for `savedIndices`. If both are provided,
   * `savedIndices` wins.
   */
  completedIndices?: number[];
  /**
   * Halve the strip's vertical footprint — smaller padding, smaller
   * digit circle, smaller label. Use on form headers where the strip
   * shouldn't dominate the page.
   */
  compact?: boolean;
  onChange?: (index: number) => void;
  className?: string;
}

/**
 * Chevron / "arrow tab" stepper with a 4-state visual machine.
 *
 *   ┌───────────┐▶┌─────────────┐▶┌──────────────┐▶┌──────────────┐
 *   │ ✓ Basic   │▶│ ⓶ Job…      │▶│ ⓷ Org…       │▶│ ⓸ Review     │
 *   └───────────┘▶└─────────────┘▶└──────────────┘▶└──────────────┘
 *      saved          ACTIVE          pending          pending
 *      green          brand blue      transparent      transparent
 *      white text     white text      muted text       muted text
 *      check icon     digit on        digit on         digit on
 *                     white circle    grey circle      grey circle
 *
 *   visited (Next without Save) renders RED instead of grey:
 *
 *      ┌───────────┐
 *      │ ! Basic   │     ← red, with an alert icon, hint that the
 *      └───────────┘       user skipped saving this step.
 *
 * The chevron is an inline `<svg><polygon>` triangle so it renders in
 * every browser regardless of Tailwind JIT pruning arbitrary clip-path
 * utilities. Click any non-pending step to jump back.
 */
export function ArrowTabStepper({
  steps,
  activeIndex,
  savedIndices,
  visitedIndices,
  completedIndices,
  compact = false,
  onChange,
  className,
}: ArrowTabStepperProps) {
  // Size tokens — `compact` halves the visual footprint.
  const tabPaddingY      = compact ? 'py-1.5' : 'py-3';
  const tabPaddingLeft   = compact ? 'pl-2.5' : 'pl-4';
  const tabPaddingRightT = compact ? 'pr-5'   : 'pr-7';
  const tabPaddingRightL = compact ? 'pr-2.5' : 'pr-4';
  const tabGap           = compact ? 'gap-2'  : 'gap-3';
  const circleClass      = compact
    ? 'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold'
    : 'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold';
  const labelClass       = compact ? 'truncate text-xs font-semibold' : 'truncate text-sm font-semibold';
  const iconSize         = compact ? 10 : 14;
  const arrowWidth       = compact ? 14 : 22;
  const arrowSlotPad     = compact ? 'pr-2' : 'pr-3';
  const arrowOverlap     = compact ? 'right-[-7px]' : 'right-[-11px]';
  const stripBgClass     = 'bg-[var(--color-bg-card)]';
  const saved = new Set(savedIndices ?? completedIndices ?? []);
  const visited = new Set(visitedIndices ?? []);

  type State = 'active' | 'saved' | 'visited' | 'pending';
  function stateOf(i: number): State {
    if (i === activeIndex) return 'active';
    if (saved.has(i)) return 'saved';
    if (visited.has(i)) return 'visited';
    return 'pending';
  }

  /**
   * Theme-aware fills + text colours. Hex fallbacks make the component
   * usable in standalone preview without the full theme CSS loaded.
   */
  function fill(s: State): string {
    if (s === 'active') return 'var(--color-primary, #3B82F6)';
    if (s === 'saved') return 'var(--color-success, #16A34A)';
    if (s === 'visited') return 'var(--color-danger, #DC2626)';
    // Pending sits on the page background. Keep it slightly distinct
    // from the strip itself so the dividers + arrows stay visible in
    // light mode.
    return 'var(--color-bg-card, #FFFFFF)';
  }

  function textColor(s: State): string {
    if (s === 'pending') return 'var(--color-text-muted, #94A3B8)';
    return '#FFFFFF';
  }

  function digitFg(s: State): string {
    if (s === 'pending') return 'var(--color-text-secondary, #475569)';
    if (s === 'active') return 'var(--color-primary, #3B82F6)';
    if (s === 'saved') return 'var(--color-success, #16A34A)';
    return 'var(--color-danger, #DC2626)';
  }

  return (
    <div
      role="tablist"
      className={cn(
        'flex w-full items-stretch overflow-hidden rounded-md border border-[var(--color-border)]',
        stripBgClass,
        className,
      )}
    >
      {steps.map((step, i) => {
        const s = stateOf(i);
        const allowed = step.enabled ?? (s !== 'pending');
        const isLast = i === steps.length - 1;
        const tabFill = fill(s);
        const tabText = textColor(s);

        return (
          <div
            key={step.key}
            className={cn('relative flex flex-1 items-stretch', !isLast && arrowSlotPad)}
          >
            <button
              type="button"
              role="tab"
              aria-selected={s === 'active'}
              disabled={!allowed}
              onClick={() => allowed && onChange?.(i)}
              style={{ background: tabFill, color: tabText }}
              className={cn(
                'relative flex w-full items-center text-left transition-colors',
                tabPaddingY,
                tabPaddingLeft,
                tabGap,
                !isLast && tabPaddingRightT,
                isLast && tabPaddingRightL,
                compact ? 'text-xs' : 'text-sm',
                allowed
                  ? 'cursor-pointer hover:brightness-110'
                  : 'cursor-not-allowed',
                s === 'active' && 'font-semibold',
              )}
            >
              <span
                className={circleClass}
                style={{
                  background:
                    s === 'pending'
                      ? 'var(--color-bg-muted, #F1F5F9)'
                      : '#FFFFFF',
                  color: digitFg(s),
                  boxShadow:
                    s !== 'pending'
                      ? '0 0 0 1px rgba(255,255,255,0.35)'
                      : 'inset 0 0 0 1px var(--color-border, #E2E8F0)',
                }}
              >
                {s === 'saved' ? (
                  <Check size={iconSize} strokeWidth={3} />
                ) : s === 'visited' ? (
                  <AlertCircle size={iconSize} strokeWidth={2.5} />
                ) : (
                  i + 1
                )}
              </span>
              <span className="flex min-w-0 flex-col">
                <span className={labelClass}>{step.label}</span>
                {step.description && !compact && (
                  <span
                    className="truncate text-[11px]"
                    style={{
                      color:
                        s === 'pending'
                          ? 'var(--color-text-muted, #94A3B8)'
                          : 'rgba(255,255,255,0.85)',
                    }}
                  >
                    {step.description}
                  </span>
                )}
              </span>
            </button>

            {/* Arrow chevron — fill matches the tab on the LEFT so the
                arrow visually extrudes the current tab into the next
                slot. Pending tabs use the strip's card colour so the
                divider line still shows. */}
            {!isLast && (
              <svg
                aria-hidden
                width={arrowWidth}
                viewBox="0 0 22 56"
                preserveAspectRatio="none"
                className={cn('pointer-events-none absolute top-0 h-full', arrowOverlap)}
                style={{ zIndex: 2 }}
              >
                <polygon points="0,0 22,28 0,56" fill={tabFill} />
              </svg>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default ArrowTabStepper;
